/**
 * @name GerenciarRestricoesAlimentares
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Componente para gerenciar restrições alimentares dos dependentes
 * @param {GerenciarRestricoesAlimentaresProps} props - Props do componente
 * @returns {JSX.Element} Componente de gerenciamento de restrições
 * @package src/components/pages/gestao-financeira/dependentes/restricoes/GerenciarRestricoesAlimentares.tsx
 */

import { Add as AddIcon, Delete as DeleteIcon, Info as InfoIcon, Warning as WarningIcon } from '@mui/icons-material';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider, IconButton, List, ListItem, ListItemSecondaryAction, ListItemText, Typography } from '@mui/material';
import React, { useState } from 'react';
import { IPessoas } from '../../../../../types/tables/IPessoas';
import ConfirmDialog from '../../../../common/dialogs/ConfirmDialog';
import ModalAdicionarRestricao from './ModalAdicionarRestricao';
import { useGerenciarRestricoesAlimentares } from './useGerenciarRestricoesAlimentares';

export interface GerenciarRestricoesAlimentaresProps {
     dependente: IPessoas;
}

const GerenciarRestricoesAlimentares: React.FC<GerenciarRestricoesAlimentaresProps> = ({ dependente }) => {
     const { restricoes, produtosDisponiveis, loading, modalAberto, abrirModal, fecharModal, adicionarRestricao, removerRestricao, formatarNomeProduto } = useGerenciarRestricoesAlimentares(dependente);
     // Estado para controle do dialog de confirmação
     const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
     const [restricaoParaExcluir, setRestricaoParaExcluir] = useState<number | null>(null);

     if (loading.geral) {
          return (
               <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
               </Box>
          );
     }

     return (
          <>
               <Card>
                    <CardContent>
                         <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant='h6'>Restrições Alimentares</Typography>
                              <Button
                                   variant='contained'
                                   startIcon={<AddIcon />}
                                   onClick={abrirModal}
                                   color='secondary'
                                   size='small'
                              >
                                   Adicionar Restrição
                              </Button>
                         </Box>

                         <Alert
                              severity='info'
                              icon={<InfoIcon />}
                              sx={{ mb: 2 }}
                         >
                              Configure produtos que {dependente.nome} não pode consumir. Estes produtos serão bloqueados automaticamente no totem da cantina.
                         </Alert>

                         {restricoes.length === 0 ? (
                              <Box sx={{ textAlign: 'center', py: 4 }}>
                                   <WarningIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                                   <Typography color='text.secondary'>Nenhuma restrição alimentar cadastrada</Typography>
                                   <Typography
                                        variant='body2'
                                        color='text.secondary'
                                        sx={{ mt: 1 }}
                                   >
                                        Adicione restrições para proteger {dependente.nome} de alimentos inadequados
                                   </Typography>
                              </Box>
                         ) : (
                              <>
                                   <Typography
                                        variant='body2'
                                        color='text.secondary'
                                        sx={{ mb: 2 }}
                                   >
                                        {restricoes.length} {restricoes.length === 1 ? 'restrição cadastrada' : 'restrições cadastradas'}
                                   </Typography>

                                   <List>
                                        {restricoes.map((restricao, index) => (
                                             <React.Fragment key={restricao.id}>
                                                  {index > 0 && <Divider />}
                                                  <ListItem>
                                                       <ListItemText
                                                            primary={formatarNomeProduto(restricao)}
                                                            secondary={
                                                                 <Box sx={{ mt: 1 }}>
                                                                      {restricao.observacoes && (
                                                                           <Typography
                                                                                variant='body2'
                                                                                color='text.secondary'
                                                                           >
                                                                                {restricao.observacoes}
                                                                           </Typography>
                                                                      )}
                                                                      <Box sx={{ mt: 0.5 }}>
                                                                           <Chip
                                                                                label={restricao.ativo ? 'Ativa' : 'Inativa'}
                                                                                size='small'
                                                                                color={restricao.ativo ? 'success' : 'default'}
                                                                                sx={{ mr: 1 }}
                                                                           />
                                                                      </Box>
                                                                 </Box>
                                                            }
                                                       />
                                                       <ListItemSecondaryAction>
                                                            {typeof restricao.id === 'number' && (
                                                                 <IconButton
                                                                      edge='end'
                                                                      aria-label='remover'
                                                                      onClick={() => {
                                                                           setRestricaoParaExcluir(restricao.id);
                                                                           setConfirmDialogOpen(true);
                                                                      }}
                                                                      disabled={loading.removendo === restricao.id}
                                                                 >
                                                                      {loading.removendo === restricao.id ? <CircularProgress size={24} /> : <DeleteIcon />}
                                                                 </IconButton>
                                                            )}
                                                            {/* Modal de confirmação de exclusão */}
                                                            <ConfirmDialog
                                                                 open={confirmDialogOpen}
                                                                 title='Remover restrição alimentar?'
                                                                 content='Tem certeza que deseja remover esta restrição? Esta ação não pode ser desfeita.'
                                                                 onConfirm={() => {
                                                                      if (restricaoParaExcluir !== null) {
                                                                           removerRestricao(restricaoParaExcluir);
                                                                      }
                                                                      setConfirmDialogOpen(false);
                                                                      setRestricaoParaExcluir(null);
                                                                 }}
                                                                 onCancel={() => {
                                                                      setConfirmDialogOpen(false);
                                                                      setRestricaoParaExcluir(null);
                                                                 }}
                                                                 confirmButtonText='Remover'
                                                                 confirmButtonColor='error'
                                                            />
                                                       </ListItemSecondaryAction>
                                                  </ListItem>
                                             </React.Fragment>
                                        ))}
                                   </List>
                              </>
                         )}
                    </CardContent>
               </Card>

               <ModalAdicionarRestricao
                    open={modalAberto}
                    onClose={fecharModal}
                    onConfirm={adicionarRestricao}
                    produtosDisponiveis={produtosDisponiveis}
                    restricoesExistentes={restricoes as any}
                    loading={loading.adicionando}
               />
          </>
     );
};

export default GerenciarRestricoesAlimentares;
